import {
  MSE_OVERALL_NOTE_KEY,
  optionLabel,
  OTHER_INTERVENTIONS_NOTE_KEY,
  PROGRESS_INTERVENTIONS_SECTIONS,
  PROGRESS_MSE_FIELDS,
  PROGRESS_RECOMMENDATION_OPTIONS,
  PROGRESS_RISK_ACT_FIELDS,
  PROGRESS_RISK_ACT_OPTIONS,
  PROGRESS_RISK_LEVEL_OPTIONS,
} from "../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/form-components/progress-note/ProgressNoteConstant";
import CptCodesBlock from "../_shared/CptCodesBlock";
import MedicationsBlock from "../_shared/MedicationsBlock";
import PrescriptionsBlock from "../_shared/PrescriptionsBlock";
import ScreeningAssessmentBlock from "../_shared/ScreeningAssessmentBlock";
import VaccinesBlock from "../_shared/VaccinesBlock";
import {
  CarePlanTemplateList,
  checkNull,
  KeyValPair,
  ListContent,
  OrderList,
  RenderTextWithContainerPattern,
  ReportSection,
  TherapyNotesList,
} from "../constant";

interface ProgressNoteTemplateProps {
  data: any;
}

// The checkbox modal persists the section as a comma-separated string of the
// selected option names, e.g. { interventionsUsed: "cbt,mindfulness" }.
const parseSelected = (value: unknown): string[] =>
  typeof value === "string"
    ? value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
    : [];

/**
 * Free text printed straight under its section band.
 *
 * Sections whose heading on the form sits directly above a single textarea get
 * this instead of <RenderTextWithContainerPattern/>, so the report does not
 * invent a sub-heading the form does not have.
 */
const SectionText = ({ val }: { val?: string | null }) => {
  if (!val?.trim()) return null;

  return (
    <div style={{ paddingLeft: 5, fontSize: "14px" }}>
      {val.split("\n").map((line, index) => (
        <p key={index} style={{ margin: "2px 0", whiteSpace: "pre-line" }}>
          {line || " "}
        </p>
      ))}
    </div>
  );
};

/** Primary Diagnosis — the ICD codes the provider selected, in picked order. */
const DiagnosisBlock = ({ data }: ProgressNoteTemplateProps) => {
  if (!checkNull(data?.assessmentCodes)) return null;

  return (
    <ListContent
      list={data?.assessmentCodes}
      renderItem={(item) => <KeyValPair k={item?.code} v={item?.description} />}
    />
  );
};

/** Current Mental Status — one line per component the provider filled in. */
const MentalStatusBlock = ({ data }: ProgressNoteTemplateProps) => {
  const filled = PROGRESS_MSE_FIELDS.map((field) => ({
    key: field.key,
    label: field.label,
    value: optionLabel(field.options, data?.[field.key]),
  })).filter((field) => !!field.value);

  const overallNote = (data?.[MSE_OVERALL_NOTE_KEY] ?? "") as string;

  if (!filled.length && !overallNote.trim()) return null;

  return (
    <div style={{ paddingLeft: 5 }}>
      {filled.map((field) => (
        <KeyValPair key={field.key} k={field.label} v={field.value as string} />
      ))}
      {overallNote.trim() && (
        <KeyValPair k="Overall MSE Note" v={overallNote} />
      )}
    </div>
  );
};

/** Risk Assessment — the denial flag, level, the act answers and the free text. */
const RiskAssessmentBlock = ({ data }: ProgressNoteTemplateProps) => {
  const level = optionLabel(PROGRESS_RISK_LEVEL_OPTIONS, data?.riskLevelOfRisk);
  const actAnswers = PROGRESS_RISK_ACT_FIELDS.map((field) => ({
    ...field,
    value: optionLabel(PROGRESS_RISK_ACT_OPTIONS, data?.[field.key]),
  })).filter((field) => !!field.value);

  const riskFactors = (data?.riskFactors ?? "") as string;
  const protectiveFactors = (data?.riskProtectiveFactors ?? "") as string;
  const additionalDetails = (data?.riskAdditionalDetails ?? "") as string;
  const deniesAll = !!data?.riskDeniesAllAreas;

  const hasContent =
    deniesAll ||
    !!level ||
    actAnswers.length > 0 ||
    riskFactors.trim() ||
    protectiveFactors.trim() ||
    additionalDetails.trim();

  if (!hasContent) return null;

  return (
    <div style={{ paddingLeft: 5 }}>
      {deniesAll && <KeyValPair k="Patient denies all areas of risk" v="Yes" />}
      {!!level && <KeyValPair k="Level of Risk" v={level} />}
      {actAnswers.map((field) => (
        <KeyValPair key={field.key} k={field.label} v={field.value as string} />
      ))}
      {riskFactors.trim() && <KeyValPair k="Risk Factors" v={riskFactors} />}
      {protectiveFactors.trim() && (
        <KeyValPair k="Protective Factors" v={protectiveFactors} />
      )}
      {additionalDetails.trim() && (
        <KeyValPair k="Additional Details" v={additionalDetails} />
      )}
    </div>
  );
};

/** Interventions Used — the selected options plus the two free-text notes. */
const InterventionsUsedBlock = ({ data }: ProgressNoteTemplateProps) => {
  const section = PROGRESS_INTERVENTIONS_SECTIONS[0];
  const selected = parseSelected(data?.[section.key]);
  const labels = section.items
    .filter((item) => selected.includes(item.name))
    .map((item) => item.label);

  const note = (data?.[section.noteKey ?? `${section.key}Note`] ??
    "") as string;
  const otherNote = (data?.[OTHER_INTERVENTIONS_NOTE_KEY] ?? "") as string;

  if (!labels.length && !note.trim() && !otherNote.trim()) return null;

  return (
    <div style={{ paddingLeft: 5 }}>
      {labels.length > 0 && (
        <p style={{ margin: "2px 0", fontSize: "14px" }}>{labels.join(", ")}</p>
      )}
      {note.trim() && (
        <p style={{ margin: "2px 0", fontSize: "14px" }}>
          <strong>Note : </strong>
          {note}
        </p>
      )}
      {otherNote.trim() && (
        <p style={{ margin: "2px 0", fontSize: "14px" }}>
          <strong>Other Interventions : </strong>
          {otherNote}
        </p>
      )}
    </div>
  );
};

/** Recommendation and the prescribed frequency of treatment. */
const RecommendationBlock = ({ data }: ProgressNoteTemplateProps) => {
  const recommendation = optionLabel(
    PROGRESS_RECOMMENDATION_OPTIONS,
    data?.recommendation
  );
  // Free text on the form, so it is printed as written rather than looked up.
  const frequency = (
    (data?.prescribedTreatmentFrequency ?? "") as string
  ).trim();

  if (!recommendation && !frequency) return null;

  return (
    <div style={{ paddingLeft: 5 }}>
      {!!recommendation && <KeyValPair k="Recommendation" v={recommendation} />}
      {!!frequency && (
        <KeyValPair k="Prescribed Frequency of Treatment" v={frequency} />
      )}
    </div>
  );
};

/**
 * Progress Note report.
 *
 * Unlike the other note templates this one does not regroup the note into
 * Subjective / Objective / Assessment / Plan bands. The Progress Note form has
 * its own section order, so the report prints one band per form section, in the
 * order the provider fills them in — Primary Diagnosis through Recommendation —
 * and every band label is the heading shown on the form.
 *
 * Keep this file in step with <ProgressNote/>: a section added, renamed or
 * reordered there must be added, renamed or reordered here too.
 *
 * The Chief Complaint is printed by <EncounterSummeryTemplate/> for every note
 * type, so it is not repeated here.
 */
const ProgressNoteTemplate = ({ data }: ProgressNoteTemplateProps) => {
  return (
    <>
      {/* ═══════════════ 1. PRIMARY DIAGNOSIS ═══════════════ */}
      <ReportSection label="Primary Diagnosis">
        <DiagnosisBlock data={data} />
        <RenderTextWithContainerPattern
          label="Diagnosis Justification"
          val={data?.diagnosisJustification}
        />
      </ReportSection>

      {/* ═══════════════ 2. CURRENT MENTAL STATUS ═══════════════ */}
      <ReportSection label="Current Mental Status">
        <MentalStatusBlock data={data} />
      </ReportSection>

      {/* ═══════════════ 3. RISK ASSESSMENT ═══════════════ */}
      <ReportSection label="Risk Assessment">
        <RiskAssessmentBlock data={data} />
      </ReportSection>

      {/* ═══════════════ 4. MEDICATIONS ═══════════════ */}
      <ReportSection label="Medications">
        {/* The "not currently taking any" flag hides the medication list on the
            form, so the report prints the flag in its place rather than
            contradicting it with chart records the note no longer claims. */}
        {data?.noCurrentMedications ? (
          <p style={{ margin: "5px 0", paddingLeft: 5, fontSize: "14px" }}>
            Patient is not currently taking any medications or supplements.
          </p>
        ) : (
          /* Medications and supplements linked to this visit, printed the same
             way every other note template prints them. */
          <MedicationsBlock data={data} />
        )}
        <RenderTextWithContainerPattern
          label="Medication Comments"
          val={data?.medicationComments}
        />
      </ReportSection>

      {/* ═══════════════ 5. SUBJECTIVE ═══════════════ */}
      <ReportSection label="Subjective Report and Symptom Description">
        <SectionText val={data?.subjectiveFreeNote} />
      </ReportSection>

      {/* ═══════════════ 6. OBJECTIVE ═══════════════ */}
      <ReportSection label="Objective Content">
        <SectionText val={data?.objectiveFreeNote} />
      </ReportSection>

      {/* ═══════════════ 7. OUTCOME MEASURES ═══════════════ */}
      <ReportSection label="Outcome Measures">
        <ScreeningAssessmentBlock data={data} />
      </ReportSection>

      {/* ═══════════════ 8. INTERVENTIONS USED ═══════════════ */}
      <ReportSection label="Interventions Used">
        <InterventionsUsedBlock data={data} />
      </ReportSection>

      {/* ═══════════════ 9. ASSESSMENT ═══════════════ */}
      <ReportSection label="Assessment">
        <RenderTextWithContainerPattern
          label="Clinical Assessment"
          val={data?.assessmentFreeNote}
        />
        <RenderTextWithContainerPattern
          label="Additional Notes"
          val={data?.assessmentAdditionalNotes}
        />
      </ReportSection>

      {/* ═══════════════ 10. PLAN ═══════════════ */}
      <ReportSection label="Plan">
        <RenderTextWithContainerPattern
          label="Plan Note"
          val={data?.planFreeNote}
        />
        <PrescriptionsBlock data={data} />
        <VaccinesBlock data={data} />
        <OrderList orders={data?.patientOrders} />
        <CptCodesBlock data={data} />
        <RenderTextWithContainerPattern
          label="Care Plan"
          val={data?.carePlan}
        />
        <CarePlanTemplateList carePlanTemplates={data?.carePlanTemplates} />
        <TherapyNotesList therapyNotes={data?.therapyNotes} />
        <RenderTextWithContainerPattern
          label="Instructions"
          val={data?.instruction}
        />
      </ReportSection>

      {/* ═══════════════ 11. RECOMMENDATION ═══════════════ */}
      <ReportSection label="Recommendation">
        <RecommendationBlock data={data} />
      </ReportSection>

      {/* ═══════════════ 12. FOLLOW UP & INSTRUCTIONS ═══════════════
          The last section on the form, printed the same way every other note
          template prints it. The F/U appointment the form can schedule beside it
          is an appointment record, not note content, so nothing is printed for
          it here. */}
      <ReportSection label="Follow-Up">
        <RenderTextWithContainerPattern
          label="Follow Up"
          val={data?.followUp}
        />
      </ReportSection>
    </>
  );
};

export default ProgressNoteTemplate;
